<template>
  <div v-if="isLoading">
    <span class="loading loading-spinner loading-lg text-base-content/60"></span>
  </div>
  <div v-else class="relative">
    <!-- Actions Section -->
    <div class="flex justify-end items-center mb-6">
      <button
        class="btn btn-outline btn-sm"
        title="Kopijuoti kelionę"
        @click="isCopyTravelModalOpen = true"
      >
        <DefaultIcon name="plus" size="w-4 h-4" />
        Kopijuoti
      </button>
    </div>

    <!-- Review Section -->
    <div class="collapse collapse-arrow bg-base-100 rounded-lg mb-6">
      <input type="checkbox" checked />
      <div class="collapse-title text-xl font-semibold text-base-content px-6">
        Kelionės informacija
      </div>
      <div class="collapse-content">
        <div class="px-2 mt-3">
          <Timeline :travelData="travelInfo">
            <template #vehicle-driver-actions>
              <button
                v-if="canEditDriverVehicle && travelInfo.status === 'travelStatus1'"
                class="btn btn-xs btn-ghost btn-circle"
                title="Keisti automobilį ir vairuotoją"
                @click="isEditDriverVehicleModalOpen = true"
              >
                <DefaultIcon name="editPencil" size="w-4 h-4" />
              </button>
            </template>
          </Timeline>
        </div>
      </div>
    </div>

    <!-- Route Section -->
    <div class="collapse collapse-arrow bg-base-100 rounded-lg mb-6">
      <input type="checkbox" checked />
      <div class="collapse-title text-xl font-semibold text-base-content px-6">
        Kelionės maršrutas
      </div>
      <div class="collapse-content">
        <div v-if="travelInfo && travelInfo.tripRoute" class="my-3 px-2">
          <p class="font-medium mb-3">Maršruto informacija</p>
          <ol class="text-sm">
            <li>
              Maršruto ilgis (km):
              {{ travelInfo.tripRoute.length?.toFixed(2) ?? "Nėra duomenų" }}
            </li>
            <li>
              Maršruto trukmė (val):
              {{
                travelInfo.duration
                  ? formatDuration(travelInfo.duration)
                  : "Nėra duomenų"
              }}
            </li>
          </ol>
        </div>
        <div class="px-2 mt-6">
          <Map
            :waypoints-geometry="travelInfo.waypoints"
            :route-geometry="routeGeometry"
            :center="[21.39, 55.71]"
            :zoom="10"
            class="rounded-md"
          />
        </div>
      </div>
    </div>

    <!-- Trips Info Section -->
    <div class="collapse collapse-arrow bg-base-100 rounded-lg mb-6">
      <input type="checkbox" />
      <div class="collapse-title text-xl font-semibold text-base-content px-6">
        Pavėžėjimų informacija
      </div>
      <div class="collapse-content">
        <div class="my-3 px-2">
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div v-for="trip in travelInfo.trips" :key="trip.id">
              <router-link
                :to="'/trips/' + trip.id + '/view'"
                class="text-primary/60 hover:text-primary"
                title="Peržiūrėti pavėžėjimo informacija"
                target="_blank"
              >
                Pavėžėjimo nr. {{ trip.id }}
              </router-link>

              <div class="text-xs mt-1">
                <div v-for="waypoint in trip.waypoints" :key="waypoint.id">
                  {{ waypoint.time }} - {{ waypoint.address }}
                </div>
              </div>
              <div class="flex items-center text-xs gap-2 mt-1">
                <span>Keleiviai:</span>
                <div class="flex items-center gap-1">
                  <span>{{ trip.person_regular_count }}</span>
                  <DefaultIcon name="passenger" size="w-4 h-4" />
                </div>
                <div class="flex items-center gap-1">
                  <span>{{ trip.person_disabled_count }}</span>
                  <DefaultIcon name="wheelchair" size="w-4 h-4" />
                </div>
              </div>
              <div class="text-xs mt-1">
                <template
                  v-for="(client, index) in trip.clients"
                  :key="client.id"
                >
                  <router-link
                    :to="`/clients/${client.id}/edit`"
                    class="hover:underline"
                  >
                    {{ client.name }}
                    {{ client.surname }} </router-link
                  >{{ index < trip.clients.length - 1 ? ", " : "" }}
                </template>
              </div>
              <div v-if="trip.child_seat_needed" class="text-xs mt-1">
                Reikalinga vaikiška kėdutė
              </div>
              <div v-if="trip.wheelchair_needed" class="text-xs mt-1">
                Reikalingas neįgaliojo vež.
              </div>
              <span
                v-if="trip.status === 'tripStatus5'"
                class="badge badge-error"
              >
                Neatvyko
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Additional Info Section -->
    <div class="collapse collapse-arrow bg-base-100 rounded-lg mb-6">
      <input type="checkbox" />
      <div class="collapse-title text-xl font-semibold text-base-content px-6">
        Papildoma informacija
      </div>
      <div class="collapse-content">
        <div class="my-3 px-2">
          <p class="font-medium mb-3">Kelionės redagavimo istorija</p>
          <ol class="text-sm">
            <li>
              Sukurta: {{ travelInfo.created_at ?? "Nėra duomenų" }},
              {{ travelInfo.created_by ?? "Nėra duomenų" }}
            </li>
            <li>
              Redaguota: {{ travelInfo.updated_at ?? "Nėra duomenų" }},
              {{ travelInfo.updated_by ?? "Nėra duomenų" }}
            </li>
          </ol>
        </div>
      </div>
    </div>
  </div>
  <!-- Modal component -->
  <CopyTravelModal
    v-if="isCopyTravelModalOpen"
    :travelId="travelId"
    :driverId="travelInfo.driver.id"
    :vehicleId="travelInfo.vehicle.id"
    :startTime="travelInfo.time_start"
    :finishTime="travelInfo.time_finish"
    @close="isCopyTravelModalOpen = false"
  />
  <EditTravelDriverVehicleModal
    v-if="isEditDriverVehicleModalOpen"
    :travel-id="travelId"
    :vehicle-id="travelInfo.vehicle.id"
    :driver-id="travelInfo.driver.id"
    :date="travelInfo.date"
    :start-time="travelInfo.time_start"
    :finish-time="travelInfo.time_finish"
    :passengers-regular-count="passengersRegularCount"
    :passengers-disabled-count="passengersDisabledCount"
    @close="isEditDriverVehicleModalOpen = false"
    @updated="handleDriverVehicleUpdated"
  />
</template>

<script setup>
import { ref, computed } from "vue";
import { useRoute } from "vue-router";
import { useTravelStore } from "@/stores/travels";
import { useAuthStore } from "@/stores/auth";
import Timeline from "@/components/timelines/TimelineDetails.vue";
import DefaultIcon from "@/components/icons/DefaultIcon.vue";

import Map from "@/components/maps/MapComponent.vue";
import { useRouteCalculation } from "@/composables/routing/useRouteCalculation";
import { useTimeManagement } from "@/composables/time/useTimeManagement";
import CopyTravelModal from "@/components/ui/modals/CopyTravelModal.vue";
import EditTravelDriverVehicleModal from "@/components/ui/modals/EditTravelDriverVehicleModal.vue";

const route = useRoute();
const travelStore = useTravelStore();
const authStore = useAuthStore();
const travelId = route.params.id;

const { calculateRoute } = useRouteCalculation();
const { formatDuration } = useTimeManagement();

const isLoading = ref(true);
const travelInfo = ref(null);

const isCopyTravelModalOpen = ref(false);
const isEditDriverVehicleModalOpen = ref(false);

const canEditDriverVehicle = computed(() =>
  ["admin", "planner"].includes(authStore.user?.role),
);

const passengersRegularCount = computed(() =>
  (travelInfo.value?.trips || []).reduce(
    (sum, trip) => sum + trip.person_regular_count,
    0,
  ),
);
const passengersDisabledCount = computed(() =>
  (travelInfo.value?.trips || []).reduce(
    (sum, trip) => sum + trip.person_disabled_count,
    0,
  ),
);

const handleDriverVehicleUpdated = async () => {
  isEditDriverVehicleModalOpen.value = false;
  await loadTripData();
};

// Fetch and transform data
const loadTripData = async () => {
  try {
    isLoading.value = true;
    const response = await travelStore.fetchTravelById(travelId);
    travelInfo.value = response;
    await updateTravelRoute();
  } catch (error) {
    console.error("Error fetching trip data:", error);
    // Handle error appropriately
  } finally {
    isLoading.value = false;
  }
};

const routeGeometry = ref(null);

const updateTravelRoute = async () => {
  try {
    if (travelInfo.value.waypoints.length > 1) {
      // Calc route
      // Calculate route if waypoints exist
      if (travelInfo.value.waypoints?.length > 1) {
        const result = await calculateRoute({
          waypoints: travelInfo.value.waypoints,
          travelPoint:
            travelInfo.value.travelLat && travelInfo.value.travelLng
              ? {
                  lat: travelInfo.value.travelLat,
                  lng: travelInfo.value.travelLng,
                }
              : null,
        });

        // Update trip info with the calculated route
        travelInfo.value.tripRoute = result.summary;
        routeGeometry.value = result.geometry;
      }
    } else {
      routeGeometry.value = null;
      travelInfo.value.tripRoute = [];
    }
  } catch (error) {
    console.error("Error updating trip waypoints:", error);
    // Handle error appropriately - maybe emit an event or show a notification
  }
};

loadTripData();
</script>
